import React, {
    useCallback, useContext, useMemo
} from 'react';
import stringUtil from 'gw-portals-util-js/StringUtil';
import { TranslatorContext } from '@jutro/locale';
import { Button, Icon, Tooltip } from '@jutro/components';
import { BreakpointTrackerContext } from '@jutro/layout';
import messages from '../SgSpmBreakdown.messages';

const CROSS = 'x';
const TICK = 'tick';

function SgSpmBreakdownTable(props) {
    const {
        SBACoverages,
        Services,
        isSameCoverLevel,
        removeCover,
        save,
        hideButtons,
        hideTooltip
    } = props;

    const translator = useContext(TranslatorContext);
    const breakpoint = useContext(BreakpointTrackerContext);
    const isMobile = breakpoint === 'tablet' || breakpoint === 'phoneWide' || breakpoint === 'phone';

    const translateMessages = useCallback((messageKey) => {
        return translator(messages[messageKey]);
    }, [translator]);

    const isSameCoverLevelWrapperFn = useCallback((coverName) => {
        if (isSameCoverLevel) {
            return isSameCoverLevel(coverName);
        }
        return false;
    }, [isSameCoverLevel]);

    const headerDataContent = useMemo(() => {
        return (
            <>
                {SBACoverages.map((SBACoverage) => {
                    return (
                        <th key={SBACoverage.name}><span>{SBACoverage.name}</span></th>
                    );
                })}
            </>
        );
    }, [SBACoverages]);

    const rowDataContent = useMemo(() => {
        return (
            <>
                {Services.map((item, index) => {
                    const itemKey = stringUtil.capitalizeFirstLetter(item);
                    return (
                        <tr key={item}>
                            <td id={item}>
                                <div className="sgBreakdownTableFeatureContainer">
                                    { !hideTooltip && (
                                        <Tooltip id={`${item}Tooltip${index}`} content={translateMessages(`${itemKey}Help`)}>
                                            <span role="button" tabIndex={0} className="sgRowHelpIcon"><Icon icon="mi-info" /></span>
                                        </Tooltip>
                                    )}
                                    <p>{translateMessages(itemKey)}</p>
                                </div>
                            </td>
                            <>
                                {SBACoverages.map((SBACoverage) => {
                                    const id = SBACoverage.name.replace(' ', '');
                                    const isServiceIncluded = SBACoverage[item];
                                    return (
                                        <td
                                            key={SBACoverage.name}
                                            id={`${item}${id}`}
                                            className={`${isSameCoverLevelWrapperFn(SBACoverage.name) ? 'sgSbaSelectedLevel' : ''}`}
                                        >
                                            {isServiceIncluded
                                                && (
                                                    <p className="tick small">
                                                        <span>{TICK}</span>
                                                    </p>
                                                )
                                            }
                                            {!isServiceIncluded
                                                && (
                                                    <p className="cross small">
                                                        <span>{CROSS}</span>
                                                    </p>
                                                )
                                            }
                                        </td>
                                    );
                                })}
                            </>
                        </tr>
                    );
                })}
            </>
        );
    }, [SBACoverages, Services, translateMessages]);

    const priceRowDataContent = useMemo(() => {
        return (
            <>
                {SBACoverages.map((SBACoverage) => {
                    return (
                        <td
                            key={`${SBACoverage.name}Price`}
                            id={`${SBACoverage.name}Price`}
                            className={`${isSameCoverLevelWrapperFn(SBACoverage.name) ? 'sgSbaSelectedLevel' : ''}`}
                        >
                            <div className="sg-cover-price-box">
                                <div className="sg-cover-prices">
                                    <div className="sg-cover-prices-annual"><span>Annual</span></div>
                                    <div className="sg-cover-prices-amount">
                                        <span>
                                            £
                                            {SBACoverage.price}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </td>
                    );
                })}
            </>
        );
    }, [SBACoverages]);

    const buttonRowDataContent = useMemo(() => {
        return !hideButtons && (
            <>
                {SBACoverages.map((SBACoverage) => {
                    return (
                        <td
                            key={`add${SBACoverage.name}Row`}
                            className={`${isSameCoverLevelWrapperFn(SBACoverage.name) ? 'sgSbaSelectedLevel' : ''}`}
                        >
                            <div id={`add${SBACoverage.name}Box`}>
                                <div className="sg-cover-price-box">
                                    <div className="sg-cover-prices">
                                        <Button
                                            id={`add${SBACoverage.name}`}
                                            type="button"
                                            onClick={() => (isSameCoverLevel(SBACoverage.name) ? removeCover() : save(SBACoverage.coverLevel))}
                                            className={`sgButton ${isSameCoverLevel(SBACoverage.name) ? 'sgSecondaryButton' : 'sgPrimaryButton'}`}
                                        >
                                            {isSameCoverLevel(SBACoverage.name) ? translator(messages.RemoveButton) : translator(messages.AddButton)}
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        </td>
                    );
                })}
            </>
        );
    }, [SBACoverages, isSameCoverLevel, removeCover, save, translator]);

    const mobileBreakdownContent = useMemo(() => {
        return (
            <>
                {SBACoverages.map((SBACoverage) => {
                    const coverName = SBACoverage.name.replace(' ', '');
                    return (
                        <p>
                            <span>
                                <b>{SBACoverage.name}</b>
                                &nbsp;
                                £
                                {SBACoverage.price}
                                &nbsp;
                                {translator(messages[`${coverName}Part1`])}
                            </span>
                            <span>{translator(messages[`${coverName}Part2`])}</span>
                        </p>
                    );
                })}
            </>
        );
    }, [SBACoverages, translator]);

    return (
        <>
            {!isMobile && (
                <div className="sgBreakdownAddonTableContainer">
                    <table className="sgBreakdownAddonTableDesktop">
                        <thead>
                            <tr>
                                <th />
                                {headerDataContent}
                            </tr>
                        </thead>
                        <tbody>
                            {rowDataContent}
                            <tr>
                                <td />
                                {priceRowDataContent}
                            </tr>
                            {!hideButtons
                        && (
                            <tr>
                                <td />
                                {buttonRowDataContent}
                            </tr>
                        )}
                        </tbody>
                    </table>
                </div>
            )}
            {isMobile && (
                <div className="sgBreakdownAddonMobileTextContainer">
                    {mobileBreakdownContent}
                </div>
            )}
        </>
    );
}
export default SgSpmBreakdownTable;
